import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to manage favorites functionality
 * @param {string} storageKey - localStorage key to store favorites (e.g., 'favoriteRadios', 'favoriteChannels')
 * @returns {Object} - favorites state and methods to manage favorites
 */
const useFavorites = (storageKey) => {
  const [favorites, setFavorites] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load favorites from localStorage on mount
  useEffect(() => {
    try {
      const storedFavorites = JSON.parse(localStorage.getItem(storageKey) || '[]');
      setFavorites(storedFavorites);
      setIsInitialized(true);
    } catch (error) {
      console.error(`Error loading favorites from ${storageKey}:`, error);
      setFavorites([]);
      setIsInitialized(true);
    }
  }, [storageKey]);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(storageKey, JSON.stringify(favorites));
    }
  }, [favorites, storageKey, isInitialized]);

  // Check if an item is in favorites
  const isFavorite = useCallback((itemId) => {
    return favorites.some(fav => fav.id === itemId);
  }, [favorites]);

  // Add an item to favorites
  const addFavorite = useCallback((item) => {
    if (!isFavorite(item.id)) {
      setFavorites(prev => [...prev, item]);
      return true;
    }
    return false;
  }, [isFavorite]);

  // Remove an item from favorites
  const removeFavorite = useCallback((itemId) => {
    if (isFavorite(itemId)) {
      setFavorites(prev => prev.filter(fav => fav.id !== itemId));
      return true;
    }
    return false;
  }, [isFavorite]);

  // Toggle an item in favorites
  const toggleFavorite = useCallback((item) => {
    if (isFavorite(item.id)) {
      removeFavorite(item.id);
      return false; // Item was removed
    } else {
      addFavorite(item);
      return true; // Item was added
    }
  }, [isFavorite, removeFavorite, addFavorite]);

  return {
    favorites,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite
  };
};

export default useFavorites;
